import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Save, RefreshCw, Send } from "lucide-react";
import { toast } from "sonner";

export default function Reminders() {
  const [templates, setTemplates] = useState([]);
  const [log, setLog] = useState([]);

  const load = async () => {
    const [t, l] = await Promise.all([api.get("/reminders/templates"), api.get("/reminders/log")]);
    setTemplates(t.data.items); setLog(l.data.items);
  };
  useEffect(() => { load(); }, []);

  const updateField = (id, key, value) => setTemplates(arr => arr.map(t => t.id === id ? { ...t, [key]: value } : t));
  const save = async (t) => {
    try { await api.patch(`/reminders/templates/${t.id}`, { subject: t.subject, body: t.body, enabled: t.enabled }); toast.success("Template saved"); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  const tick = async () => { try { await api.post("/reminders/run-tick"); toast.success("Scheduler tick ran"); load(); } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); } };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-end justify-between">
        <div>
          <div className="section-label">Communication</div>
          <h1 className="font-heading text-3xl font-semibold">Reminder templates & log</h1>
          <p className="mt-1 text-sm text-ink-muted">Scheduler runs hourly · also covers birthday + 2-day OPD follow-up nudges.</p>
        </div>
        <Button variant="outline" onClick={tick} data-testid="reminders-tick-btn"><RefreshCw size={14} className="mr-2" />Run scheduler now</Button>
      </div>

      <Tabs defaultValue="templates">
        <TabsList>
          <TabsTrigger value="templates" data-testid="rem-tab-templates">Templates</TabsTrigger>
          <TabsTrigger value="log" data-testid="rem-tab-log">Send log</TabsTrigger>
        </TabsList>

        <TabsContent value="templates" className="mt-4 space-y-3">
          {templates.map(t => (
            <Card key={t.id} className="p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="font-heading text-base font-semibold">{t.key}</div>
                  <div className="text-xs text-ink-muted">Channel: <b>{t.channel}</b> · Variables: {"{patient_name} {uhid} {hospital_name} {scheduled_date}"}</div>
                </div>
                <label className="flex items-center gap-2 text-xs"><Switch checked={!!t.enabled} onCheckedChange={(v) => updateField(t.id, "enabled", v)} />Enabled</label>
              </div>
              {t.channel === "email" && (<div className="mt-3"><Label>Subject</Label><Input value={t.subject || ""} onChange={(e) => updateField(t.id, "subject", e.target.value)} /></div>)}
              <div className="mt-2"><Label>Body</Label><Textarea rows={3} value={t.body || ""} onChange={(e) => updateField(t.id, "body", e.target.value)} /></div>
              <div className="mt-3 flex justify-end"><Button size="sm" onClick={() => save(t)} className="bg-brand hover:bg-brand-700" data-testid={`rem-save-${t.key}`}><Save size={12} className="mr-1" />Save</Button></div>
            </Card>
          ))}
          {templates.length === 0 && <Card className="p-6 text-center text-sm text-ink-muted">No templates.</Card>}
        </TabsContent>

        <TabsContent value="log" className="mt-4">
          <Card className="overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-secondary text-xs uppercase tracking-wider text-ink-muted"><tr><th className="px-3 py-2 text-left">When</th><th className="px-3 py-2 text-left">Channel</th><th className="px-3 py-2 text-left">Template</th><th className="px-3 py-2 text-left">To</th><th className="px-3 py-2 text-left">Body</th><th className="px-3 py-2 text-left">Status</th></tr></thead>
              <tbody className="divide-y divide-border">
                {log.length === 0 && <tr><td colSpan="6" className="px-3 py-10 text-center text-ink-muted">No reminders sent yet.</td></tr>}
                {log.slice(0, 100).map(r => (
                  <tr key={r.id}><td className="px-3 py-2 text-xs">{(r.queued_at||"").slice(0,16).replace("T"," ")}</td><td className="px-3 py-2 capitalize">{r.channel}</td><td className="px-3 py-2">{r.template_key}</td><td className="px-3 py-2 text-xs">{r.to_mobile || r.to_email}</td><td className="px-3 py-2 text-[11px] max-w-md truncate">{r.body}</td><td className="px-3 py-2 text-xs">{r.status}</td></tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
